import {
  ChatLayout,
  ChatLayoutConversation,
  ChatLayoutWidgetActions,
  ChatLayoutWidgets,
} from '@/components/chat/ChatLayout';
import {
  CORPUS_QA,
  DATAVOYAGER,
  MULTI_TOOL,
  PAPER_FINDER,
  TABLE_ANNOTATION,
} from 'configs/NoraVariants';
import { CorpusQAChat } from '@/components/chat/CorpusQAChat';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { getPlatformLabel } from '@/utils/variantBasedLabels';
import {
  getUserFromGraphQL,
  GQL_USER_FRAGMENT,
  GQL_USER_FRAGMENT_NAME,
} from '@/models/User';
import { client as gqlClient } from '@/api/GraphQLClient';
import { Message } from '@/models/Message';
import { MultiToolChat } from '@/components/chat/MultitoolChat';
import { optAppConfig } from '@/utils/config';
import { PaperFinderChat } from '@/components/chat/PaperFinderChat';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import { setCurrentThread } from '@/store/reducers/UIState';
import { Thread, ThreadId } from '@/models/Thread';
import { TrackUser } from '@/analytics/TrackUser';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useInitializeStepProgress } from '@/components/chat/chatThreadHooks';
import { WidgetMenu } from '@/components/widgets/WidgetMenu';
import { WidgetPanel } from '@/components/widgets/WidgetPanel';

import { ApolloProvider, gql, useQuery } from '@apollo/client';
import { Button, styled } from '@mui/material';
import { Fragment, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

const NOOP = () => Promise.resolve();

export const SharePage = () => {
  return (
    <ApolloProvider client={gqlClient}>
      <ShareComp />
      <TrackUser />
    </ApolloProvider>
  );
};

export const ShareComp = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pathThreadId = parseThreadIdFromPath(location.pathname);
  const noraExperienceVariant = optAppConfig((_) => _.experience.variant);
  const shouldRenderWidgets =
    noraExperienceVariant === MULTI_TOOL ||
    noraExperienceVariant === TABLE_ANNOTATION ||
    noraExperienceVariant === PAPER_FINDER ||
    noraExperienceVariant === DATAVOYAGER;

  const shouldRenderWidgetMenu =
    noraExperienceVariant === MULTI_TOOL ||
    noraExperienceVariant === TABLE_ANNOTATION ||
    noraExperienceVariant === DATAVOYAGER;

  // load step progress so we know if there is an error in Report widget for Corpus QA experience
  useInitializeStepProgress(pathThreadId ?? null);

  const {
    loading: isLoading,
    error,
    data,
  } = useQuery(GET_MESSAGES_QUERY, {
    variables: {
      threadId: pathThreadId,
    },
  });

  if (error) {
    // HACK: Didn't want to build nice error UI
    throw error;
  }

  const thread = data?.thread ? getThreadFromGQL(data?.thread) : null;

  useEffect(() => {
    dispatch(setCurrentThread(thread));
  }, [thread]);

  const shouldOpenWidgetPanel = useAppSelector(
    (state) => state.UIState.openWidgetIds.length > 0,
  );

  const getShareText = () => {
    switch (noraExperienceVariant) {
      case PAPER_FINDER:
        return 'Search for papers with Paper Finder';
      case CORPUS_QA:
        return 'Ask a literature review question with Scholar QA';
      default:
        return (
          'Start a conversation with ' + getPlatformLabel(noraExperienceVariant)
        );
    }
  };

  const shareText = getShareText();

  // render different chat experiences depending on if we are in the multi-tool or corpus-qa experience
  const renderChat = (): ReactNode => {
    if (!thread) {
      getLayoverLogger().logError(
        `Could not fetch thread from GQL [threadId=${pathThreadId}]`,
      );
      return null;
    }

    switch (noraExperienceVariant) {
      case MULTI_TOOL: {
        return (
          <MultiToolChat
            thread={thread}
            isMessageBarPending={false}
            onSendMessage={NOOP}
          />
        );
      }
      case CORPUS_QA: {
        return <CorpusQAChat thread={thread} />;
      }
      case PAPER_FINDER: {
        return (
          <PaperFinderChat
            thread={thread}
            isMessageBarPending={false}
            onSendMessage={NOOP}
          />
        );
      }

      default: {
        return (
          <MultiToolChat
            thread={thread}
            isMessageBarPending={false}
            onSendMessage={NOOP}
          />
        );
      }
    }
  };

  return (
    <PermissionsContext.Provider value={{ isReadOnly: true }}>
      <ChatContainer>
        <ChatLayout
          className={classNames({
            'chat-layout--widgets-open': shouldOpenWidgetPanel,
          })}
        >
          <ChatLayoutConversation>
            <ChatContainer>
              {isLoading ? (
                <LoadingContainer>
                  <div>Loading...</div>
                </LoadingContainer>
              ) : (
                renderChat()
              )}
            </ChatContainer>
          </ChatLayoutConversation>
          {shouldRenderWidgets && (
            <Fragment>
              <ChatLayoutWidgets className="chat-layout-widgets--share-page-spacing">
                {shouldOpenWidgetPanel && <WidgetPanel />}
              </ChatLayoutWidgets>
              {shouldRenderWidgetMenu && (
                <ChatLayoutWidgetActions className="chat-layout-widget-actions--top-padding">
                  <WidgetMenu />
                </ChatLayoutWidgetActions>
              )}
            </Fragment>
          )}
        </ChatLayout>
      </ChatContainer>
      <a href="/chat">
        <CTABar>
          <CTAButton>{shareText}</CTAButton>
        </CTABar>
      </a>
    </PermissionsContext.Provider>
  );
};

const ChatContainer = styled('div')`
  background-color: #032629; // TODO: replace once varnish updates dark-blue-100 in theme
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  & > * {
    flex: 1 1 auto;
  }
`;

const LoadingContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 100%;
  width: 100%;
  color: #ffffff;
`;

const CTABar = styled('div')`
  background-color: #08232b; // not in theme
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: ${({ theme }) => theme.spacing(3)};
  position: sticky;
  right: 0;
  z-index: 1;
`;

const CTAButton = styled(Button)`
  background-color: ${({ theme }) => theme.color['green-100'].hex};
  color: ${({ theme }) => theme.color['dark-teal-100'].hex};
  font-weight: 500;
  padding: ${({ theme }) => theme.spacing(1, 2.75)};

  &:hover {
    background-color: ${({ theme }) => theme.color['green-90'].hex};
  }
`;

export const parseThreadIdFromPath = (path: string): ThreadId | null => {
  const match = path.match(/\/share\/(.+)/);
  return match ? match[1] : null;
};

const GET_MESSAGES_QUERY = gql`
  ${GQL_USER_FRAGMENT}

  query GetMessagesQuery($threadId: ID!) {
    thread(id: $threadId) {
      id
      messages {
        edges {
          node {
            id
            sender {
              ...${GQL_USER_FRAGMENT_NAME}
            }
            text
            annotatedText
            timestamp
            hasToolCalls
          }
        }
      }
    }
  }
`;

export function getMessageFromGraphQL(node: any, threadId: ThreadId): Message {
  const sender = getUserFromGraphQL(node.sender);
  const message: Message = {
    id: node.id,
    threadId,
    sender,
    text: node.text,
    timestamp: node.timestamp,
    strippedText: node.text,
    structuredText: node.annotatedText,
    metadata: {},
    viewerReaction: null,
    hasToolCalls: node.hasToolCalls,
  };
  return message;
}

export function getThreadFromGQL(args: {
  id: string;
  messages: {
    edges: any[];
  };
}): Thread {
  return {
    id: args.id,
    name: '',
    messages:
      args.messages.edges.map((edge) =>
        getMessageFromGraphQL(edge.node, args.id),
      ) ?? [],
    participantUserIds: [],
    uiState: null,
  };
}
